import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import StatusBadge from '../../components/invoice/StatusBadge';
import JobResultTable from '../../components/invoice/JobResultTable';
import PollingStatusBanner from '../../components/invoice/PollingStatusBanner';
import { useInvoicePolling } from '../../hooks/useInvoicePolling';
import { useToast } from '../../context/ToastContext';
import invoiceService from '../../services/invoiceService';
import { formatDateTime, formatDuration, formatMonthLabel } from '../../utils/invoiceFormatters';

const InvoiceJobResult = ({ source = 'ECMS' }) => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { error: toastError, success: toastSuccess, info: toastInfo } = useToast();
  const showToast = (msg, type) => {
    if (type === 'success') toastSuccess(msg);
    else if (type === 'error') toastError(msg);
    else toastInfo(msg);
  };
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pollingError, setPollingError] = useState(false);

  // Initial load
  useEffect(() => {
    const load = async () => {
      try {
        const res = await invoiceService.getJob(jobId);
        setJob(res.data);
      } catch {
        setError('Failed to load job details.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [jobId]);

  const handleUpdate = useCallback((updatedJob, err) => {
    if (err) {
      setPollingError(true);
      return;
    }
    setPollingError(false);
    if (updatedJob) setJob(updatedJob);
  }, []);

  const handleComplete = useCallback((finalJob) => {
    setJob(finalJob);
    if (finalJob.status === 'COMPLETED') {
      showToast(`Generation completed. ${finalJob.generatedCount} invoices generated.`, 'success');
    } else if (finalJob.status === 'COMPLETED_WITH_ERRORS') {
      showToast(`Generation completed with ${finalJob.failedCount} error(s).`, 'warning');
    } else if (finalJob.status === 'FAILED') {
      showToast('Generation failed.', 'error');
    }
  }, [showToast]);

  // Start polling only if job is RUNNING
  const shouldPoll = job?.status === 'RUNNING';
  useInvoicePolling(shouldPoll ? jobId : null, handleUpdate, handleComplete);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        
        <div className="w-full px-4 py-8">
          <div className="h-8 bg-gray-200 rounded animate-pulse w-48 mb-6" />
          <div className="bg-white rounded-xl border border-gray-100 p-8">
            <div className="space-y-3">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="h-8 bg-gray-100 rounded animate-pulse" />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !job) {
    return (
      <div className="min-h-screen bg-gray-50">
        
        <div className="w-full px-4 py-8 text-center">
          <p className="text-gray-500 mb-4">{error || 'Job not found.'}</p>
          <button onClick={() => navigate(`/admin/${source.toLowerCase()}/invoices/jobs`)}
            className="text-sm text-primary border border-primary px-4 py-2 rounded-lg hover:bg-primary/5">
            Back to History
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      
      <div className="w-full px-4 py-8">

        {/* Back + title */}
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => navigate(`/admin/${source.toLowerCase()}/invoices/jobs`)}
            className="text-gray-400 hover:text-gray-600 text-sm">← Back</button>
          <div>
            <h1 className="text-xl font-bold text-gray-800">
              Generation Job #{job.id}
            </h1>
            <p className="text-xs text-gray-400 mt-0.5">
              {formatMonthLabel(job.invoiceMonth, job.invoiceYear)} &middot; {job.jobType}
            </p>
          </div>
        </div>

        {/* Polling banner */}
        <PollingStatusBanner status={job.status} hasError={pollingError} />

        {/* Summary cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          {[
            { label: 'Total', value: job.totalEnterprises, color: '#1F2937' },
            { label: 'Generated', value: job.generatedCount, color: '#27500A' },
            { label: 'Skipped', value: job.skippedCount, color: '#6B7280' },
            { label: 'Failed', value: job.failedCount, color: '#A32D2D' },
          ].map(card => (
            <div key={card.label} className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
              <p className="text-xs text-gray-500 mb-1">{card.label}</p>
              <p className="text-3xl font-bold" style={{ color: card.color }}>{card.value ?? 0}</p>
            </div>
          ))}
        </div>

        {/* Job metadata */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 mb-5">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-xs text-gray-400 mb-0.5">Type</p>
              <StatusBadge status={job.jobType} />
            </div>
            <div>
              <p className="text-xs text-gray-400 mb-0.5">Status</p>
              <StatusBadge status={job.status} pulse={job.status === 'RUNNING'} />
            </div>
            <div>
              <p className="text-xs text-gray-400 mb-0.5">Triggered By</p>
              <p className="text-gray-700 font-medium">{job.triggeredByName || 'Scheduler'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-400 mb-0.5">Started At</p>
              <p className="text-gray-700">{formatDateTime(job.startedAt)}</p>
            </div>
            {job.completedAt && (
              <div>
                <p className="text-xs text-gray-400 mb-0.5">Completed At</p>
                <p className="text-gray-700">{formatDateTime(job.completedAt)}</p>
              </div>
            )}
            {job.durationSeconds != null && (
              <div>
                <p className="text-xs text-gray-400 mb-0.5">Duration</p>
                <p className="text-gray-700">{formatDuration(job.durationSeconds)}</p>
              </div>
            )}
          </div>
        </div>

        {/* Results table */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100">
            <p className="text-sm font-semibold text-gray-700">Enterprise Results</p>
            <p className="text-xs text-gray-400">{(job.details || []).length} enterprises processed</p>
          </div>
          <JobResultTable details={job.details || []} loading={false} />
        </div>
      </div>
    </div>
  );
};

export default InvoiceJobResult;
